/**
 * HRPage - Human Resources Management System
 * Refactored to use modular components
 */
import { useState, useEffect } from 'react';
import { Users, Calendar, Award, BookOpen, MessageSquare, TrendingUp, Target, AlertOctagon, CalendarDays, Briefcase, DoorOpen, Gift, Star, Activity } from 'lucide-react';
import axios from 'axios';

// Import modular HR components
import {
  API, MODAL_TITLES,
  Modal, Tab,
  DashboardTab, DynamicForm, TimesheetImportResult, AttendanceByStaff,
  KPITab, PerformanceTab, LeaveTab, TrainingTab,
  DisciplinaryTab, SkillTab, ContractTab, MeetingTab, RewardTab, ExitTab
} from '../components/hr';
import OfficeSettingsCard from '../components/hr/OfficeSettingsCard';
import ExportButton from '../components/ExportButton';
import TodayAttendanceWidget from '../components/TodayAttendanceWidget';
import WingmanActivityPage from './WingmanActivityPage';

const HRPage = () => {
  const [tab, setTab] = useState('dashboard');
  const [attendanceSubTab, setAttendanceSubTab] = useState('records'); // records | wingman
  const [dash, setDash] = useState(null);
  const [staff, setStaff] = useState([]);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, type: '', item: null });
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Fetch all HR data
  const fetchAll = async () => {
    try {
      setLoading(true);
      const [dashRes, staffRes, perfRes, attRes, trainRes, consultRes, kpiRes, discRes, leaveRes, skillRes, contractRes, meetRes, exitRes, rewardRes] = await Promise.all([
        axios.get(`${API}/hr/dashboard`),
        axios.get(`${API}/staff`),
        axios.get(`${API}/hr/performance`),
        axios.get(`${API}/hr/attendance`),
        axios.get(`${API}/hr/training`),
        axios.get(`${API}/hr/consultation`),
        axios.get(`${API}/hr/kpi`),
        axios.get(`${API}/hr/disciplinary`),
        axios.get(`${API}/hr/leave`),
        axios.get(`${API}/hr/skill`),
        axios.get(`${API}/hr/contract`),
        axios.get(`${API}/hr/meeting`),
        axios.get(`${API}/hr/exit-interview`),
        axios.get(`${API}/hr/reward`)
      ]);
      
      setDash(dashRes.data);
      setStaff(staffRes.data || []);
      setData({
        performance: perfRes.data,
        attendance: attRes.data,
        training: trainRes.data,
        consultation: consultRes.data,
        kpi: kpiRes.data,
        disciplinary: discRes.data,
        leave: leaveRes.data,
        skill: skillRes.data,
        contract: contractRes.data,
        meeting: meetRes.data,
        exit: exitRes.data,
        reward: rewardRes.data
      });
    } catch (e) {
      console.error('Error fetching HR data:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // Modal handlers
  const openModal = (type, item = null) => setModal({ open: true, type, item });
  const closeModal = () => setModal({ open: false, type: '', item: null });

  // CRUD handlers
  const getApiType = (type) => type === 'exit' ? 'exit-interview' : type;
  
  const handleSave = async (type, formData) => {
    try {
      const apiType = getApiType(type);
      if (modal.item) {
        await axios.put(`${API}/hr/${apiType}/${modal.item.id}`, formData);
      } else {
        await axios.post(`${API}/hr/${apiType}`, formData);
      }
      await fetchAll();
      closeModal();
    } catch (e) {
      alert('Gagal menyimpan');
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Padam rekod ini?')) return;
    try {
      const apiType = getApiType(type);
      await axios.delete(`${API}/hr/${apiType}/${id}`);
      await fetchAll();
    } catch (e) {
      alert('Gagal memadam');
    }
  };

  // Import handler
  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/hr/attendance/import`, formData);
      // PDF timesheet returns rich analytics
      if (Array.isArray(res.data.results) && res.data.results.length > 0) {
        setImportResult(res.data);
      } else {
        alert(`Berjaya import ${res.data.imported} rekod.${res.data.total_errors > 0 ? ` ${res.data.total_errors} ralat.` : ''}`);
      }
      await fetchAll();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Gagal import';
      alert(msg);
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700" />
      </div>
    );
  }

  const s = dash?.summary || {};

  return (
    <div className="space-y-6" data-testid="hr-page">
      {/* Header */}
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Users className="w-6 h-6 sm:w-7 sm:h-7 text-red-700" /> HR Hub
          </h1>
          <p className="text-gray-500 text-xs sm:text-sm">Sistem pengurusan HR lengkap — 4 kategori • 12 modul</p>
        </div>
        <ExportButton type="hr" />
      </div>

      {/* Real-time clock-in snapshot (from GPS clock-in / Self-Service Portal) */}
      <TodayAttendanceWidget />

      {/* Primary category tabs — group 12 modules into 4 broader categories */}
      <div
        className="flex gap-1.5 bg-white rounded-xl p-1.5 border border-gray-100 shadow-sm overflow-x-auto"
        data-testid="hr-category-tabs"
      >
        {[
          { id: 'dashboard', label: 'Dashboard', icon: TrendingUp, tabs: ['dashboard'] },
          { id: 'development', label: 'Pembangunan', icon: Award, tabs: ['kpi', 'performance', 'training', 'skill'] },
          { id: 'compliance', label: 'Compliance', icon: Calendar, tabs: ['attendance', 'leave', 'disciplinary', 'contract', 'exit'] },
          { id: 'engagement', label: 'Engagement', icon: Gift, tabs: ['meeting', 'reward'] },
        ].map((cat) => {
          const Icon = cat.icon;
          const active = cat.tabs.includes(tab);
          return (
            <button
              key={cat.id}
              onClick={() => setTab(cat.tabs[0])}
              data-testid={`hr-category-${cat.id}`}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium whitespace-nowrap flex-shrink-0 transition-all ${
                active ? 'bg-red-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{cat.label}</span>
            </button>
          );
        })}
      </div>

      {/* Secondary tabs (sub-tabs within active category) — horizontal scroll on mobile */}
      <div
        className="flex gap-2 overflow-x-auto pb-2 -mx-3 px-3 sm:mx-0 sm:px-0 scrollbar-thin scroll-smooth snap-x"
        data-testid="hr-tabs"
      >
        {/* Dashboard category has no sub-tabs */}
        {['kpi', 'performance', 'training', 'skill'].includes(tab) && (
          <>
            <Tab active={tab === 'kpi'} onClick={() => setTab('kpi')} icon={Target} label="KPI" count={data.kpi?.length} />
            <Tab active={tab === 'performance'} onClick={() => setTab('performance')} icon={Award} label="Penilaian" count={data.performance?.length} />
            <Tab active={tab === 'training'} onClick={() => setTab('training')} icon={BookOpen} label="Latihan" count={s.pending_trainings} />
            <Tab active={tab === 'skill'} onClick={() => setTab('skill')} icon={Star} label="Kemahiran" />
          </>
        )}
        {['attendance', 'leave', 'disciplinary', 'contract', 'exit'].includes(tab) && (
          <>
            <Tab active={tab === 'attendance'} onClick={() => setTab('attendance')} icon={Calendar} label="Kehadiran" />
            <Tab active={tab === 'leave'} onClick={() => setTab('leave')} icon={CalendarDays} label="Cuti" count={s.pending_leaves} />
            <Tab active={tab === 'disciplinary'} onClick={() => setTab('disciplinary')} icon={AlertOctagon} label="Disiplin" count={s.active_warnings} />
            <Tab active={tab === 'contract'} onClick={() => setTab('contract')} icon={Briefcase} label="Kontrak" count={(s.probation_ending || 0) + (s.contracts_ending || 0)} />
            <Tab active={tab === 'exit'} onClick={() => setTab('exit')} icon={DoorOpen} label="Exit" count={s.turnover_ytd} />
          </>
        )}
        {['meeting', 'reward'].includes(tab) && (
          <>
            <Tab active={tab === 'meeting'} onClick={() => setTab('meeting')} icon={MessageSquare} label="1-on-1" />
            <Tab active={tab === 'reward'} onClick={() => setTab('reward')} icon={Gift} label="Ganjaran" count={data.reward?.length} />
          </>
        )}
      </div>

      {/* Tab Content */}
      {tab === 'dashboard' && (
        <>
          <OfficeSettingsCard />
          <DashboardTab dash={dash} />
        </>
      )}
      
      {tab === 'kpi' && (
        <KPITab 
          data={data.kpi} 
          onAdd={() => openModal('kpi')} 
          onEdit={i => openModal('kpi', i)} 
          onDelete={id => handleDelete('kpi', id)} 
        />
      )}
      
      {tab === 'performance' && (
        <PerformanceTab 
          data={data.performance} 
          onAdd={() => openModal('performance')} 
          onEdit={i => openModal('performance', i)} 
          onDelete={id => handleDelete('performance', id)} 
        />
      )}
      
      {tab === 'attendance' && (
        <div className="space-y-4">
          {/* Sub-tab toggle: Rekod Kehadiran vs Wingman HR Agent */}
          <div className="flex gap-2 border border-gray-200 rounded-lg p-1 bg-gray-50 w-fit" data-testid="attendance-sub-tabs">
            <button
              onClick={() => setAttendanceSubTab('records')}
              data-testid="att-sub-records"
              className={`px-4 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 transition ${attendanceSubTab === 'records' ? 'bg-white text-red-700 shadow-sm' : 'text-gray-600'}`}
            >
              <Calendar className="w-4 h-4" /> Rekod Kehadiran
            </button>
            <button
              onClick={() => setAttendanceSubTab('wingman')}
              data-testid="att-sub-wingman"
              className={`px-4 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 transition ${attendanceSubTab === 'wingman' ? 'bg-white text-red-700 shadow-sm' : 'text-gray-600'}`}
            >
              <Activity className="w-4 h-4" /> Wingman HR Agent
              <span className="ml-1 px-1 py-0.5 bg-amber-100 text-amber-700 rounded text-[9px] font-semibold">NEW</span>
            </button>
          </div>

          {attendanceSubTab === 'records' && (
            <AttendanceByStaff
              records={data.attendance}
              onAdd={() => openModal('attendance')}
              onEdit={i => openModal('attendance', i)}
              onDelete={id => handleDelete('attendance', id)}
              onImport={handleImport}
              importing={importing}
            />
          )}

          {attendanceSubTab === 'wingman' && <WingmanActivityPage />}
        </div>
      )}
      
      {tab === 'leave' && (
        <LeaveTab 
          data={data.leave} 
          onAdd={() => openModal('leave')} 
          onEdit={i => openModal('leave', i)} 
          onDelete={id => handleDelete('leave', id)}
          onChange={fetchAll}
        />
      )}
      
      {tab === 'training' && (
        <TrainingTab 
          data={data.training} 
          onAdd={() => openModal('training')} 
          onEdit={i => openModal('training', i)} 
          onDelete={id => handleDelete('training', id)} 
        />
      )}
      
      {tab === 'disciplinary' && (
        <DisciplinaryTab 
          data={data.disciplinary} 
          onAdd={() => openModal('disciplinary')} 
          onEdit={i => openModal('disciplinary', i)} 
          onDelete={id => handleDelete('disciplinary', id)} 
        />
      )}
      
      {tab === 'skill' && (
        <SkillTab 
          data={data.skill} 
          onAdd={() => openModal('skill')} 
          onEdit={i => openModal('skill', i)} 
          onDelete={id => handleDelete('skill', id)} 
        />
      )}
      
      {tab === 'contract' && (
        <ContractTab 
          data={data.contract} 
          onAdd={() => openModal('contract')} 
          onEdit={i => openModal('contract', i)} 
          onDelete={id => handleDelete('contract', id)} 
        />
      )}
      
      {tab === 'meeting' && (
        <MeetingTab 
          data={data.meeting} 
          onAdd={() => openModal('meeting')} 
          onEdit={i => openModal('meeting', i)} 
          onDelete={id => handleDelete('meeting', id)} 
        />
      )}
      
      {tab === 'reward' && (
        <RewardTab 
          data={data.reward} 
          onAdd={() => openModal('reward')} 
          onEdit={i => openModal('reward', i)} 
          onDelete={id => handleDelete('reward', id)} 
        />
      )}
      
      {tab === 'exit' && (
        <ExitTab 
          data={data.exit} 
          onAdd={() => openModal('exit')} 
          onDelete={id => handleDelete('exit', id)} 
        />
      )}

      {/* Modal for Forms */}
      <Modal 
        isOpen={modal.open} 
        onClose={closeModal} 
        title={`${modal.item ? 'Edit' : 'Tambah'} ${MODAL_TITLES[modal.type] || ''}`} 
        wide={['performance', 'exit'].includes(modal.type)}
      >
        <DynamicForm 
          type={modal.type} 
          item={modal.item} 
          staff={staff} 
          dash={dash} 
          onSave={(d) => handleSave(modal.type, d)} 
          onClose={closeModal} 
        />
      </Modal>

      {/* Timesheet PDF Import Analytics */}
      <TimesheetImportResult
        result={importResult}
        onClose={() => setImportResult(null)}
      />
    </div>
  );
};

export default HRPage;
